import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, AlertTriangle, Download, Loader2, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin Document Recovery Report.
 *
 * Lists every document whose original file is gone from BOTH disk and GridFS
 * (unrecoverable historical uploads from before durable storage existed).
 * Staff use this as a re-collection checklist and can export it as CSV.
 */
export default function MissingDocuments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/lost-documents`, { headers });
      setRows(res.data?.documents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load recovery report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.person_name || '').toLowerCase().includes(q) ||
      (r.document_type || '').toLowerCase().includes(q) ||
      (r.document_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const byPerson = useMemo(() => {
    const set = new Set(rows.map((r) => r.person_id || r.person_name));
    return set.size;
  }, [rows]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/lost-documents.csv`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'missing-documents-report.csv';
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="missing-documents-page">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={24} />
            Document Recovery Report
          </h1>
          <p className="text-sm text-muted-foreground">
            Files that are gone from the server and must be re-collected & re-uploaded.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-3xl font-semibold text-red-600" data-testid="missing-total">{rows.length}</div>
          <div className="text-sm text-muted-foreground">Documents to re-upload</div>
        </Card>
        <Card className="p-4">
          <div className="text-3xl font-semibold">{byPerson}</div>
          <div className="text-sm text-muted-foreground">People affected</div>
        </Card>
        <Card className="p-4 flex flex-col justify-center gap-2">
          <Button onClick={exportCsv} disabled={!rows.length} data-testid="export-csv-btn" className="w-full">
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
          <Button variant="outline" onClick={load} className="w-full" data-testid="refresh-btn">
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
        </Card>
      </div>

      <Card className="p-4 space-y-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search person or document type…"
            className="pl-9"
            data-testid="missing-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" /> Loading report…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground" data-testid="missing-empty">
            {rows.length === 0
              ? '🎉 No missing files — every document has a durable copy.'
              : 'No matches for your search.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="missing-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-4 font-medium">Person</th>
                  <th className="py-2 pr-4 font-medium">Document Type</th>
                  <th className="py-2 pr-4 font-medium">Uploaded</th>
                  <th className="py-2 pr-4 font-medium">Expiration</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.document_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`missing-row-${r.document_id}`}>
                    <td className="py-2 pr-4 font-medium">
                      {r.person_id ? (
                        <Link to={`/personnel/${r.person_id}`} className="text-primary hover:underline">
                          {r.person_name}
                        </Link>
                      ) : r.person_name}
                    </td>
                    <td className="py-2 pr-4">{r.document_type}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {r.expiration_date || '—'}
                    </td>
                    <td className="py-2 pr-4">
                      {r.archived
                        ? <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">Archived</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Active — re-upload</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
